import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Camera,
  PlusCircle,
  ShoppingBag,
  Briefcase,
  User,
  Compass,
  Inbox,
  X,
  Home,
  Video,
  Users,
  UserCheck,
  Bookmark,
  LayoutDashboard,
  FileText,
  Send
} from "lucide-react";
import { getMarketplaceProfile } from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showErrorToast } from "../../atoms/Utlis/Toast";
import kwikMemoriesLogoIcon from "../../../assets/Icons/login/kwikmemories_logo.webp";
import kwikMemoriesWhiteLogoIcon from "../../../assets/images/marketplace/KM_whitelogo.webp";
import { useMarketplaceTheme } from "./MarketplaceTheme";
import { isProfileCompleted, isAccountFrozen } from "./MarketplaceShared";

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

interface SidebarItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiresProfile: boolean;
}

const MarketplaceSidebar: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useMarketplaceTheme();
  const location = useLocation();
  const [loggedIn, setLoggedIn] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleToggle = () => setIsOpen((prev) => !prev);
    const handleClose = () => setIsOpen(false);
    window.addEventListener("toggle-marketplace-sidebar", handleToggle);
    window.addEventListener("close-marketplace-sidebar", handleClose);
    return () => {
      window.removeEventListener("toggle-marketplace-sidebar", handleToggle);
      window.removeEventListener("close-marketplace-sidebar", handleClose);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const handleAuthChange = () => {
      const token = localStorage.getItem("access_token");
      setLoggedIn(!!token);

      if (token) {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const prof = parsed?.data || parsed;
            setHasProfile(isProfileCompleted(prof));
          } catch {
            setHasProfile(false);
          }
        }

        getMarketplaceProfile()
          .then((res) => {
            if (!isMounted) return; 
            const profileData = res?.data || null;
            const hasData = isProfileCompleted(profileData);
            setHasProfile(hasData);
            if (hasData) {
              localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
            } else {
              localStorage.removeItem("marketplace_profile");
            }
          })
          .catch(() => {
            if (!isMounted) return; 
            const stillCached = localStorage.getItem("marketplace_profile");
            if (stillCached) {
              try {
                const parsed = JSON.parse(stillCached);
                const prof = parsed?.data || parsed;
                const valid = isProfileCompleted(prof);
                setHasProfile(valid);
                if (!valid) localStorage.removeItem("marketplace_profile");
              } catch {
                setHasProfile(false);
                localStorage.removeItem("marketplace_profile");
              }
            } else {
              setHasProfile(false);
            }
          });
      } else {
        setHasProfile(false);
      }
    };

    handleAuthChange();
    window.addEventListener("marketplace-auth-change", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);
    return () => {
      isMounted = false;
      window.removeEventListener("marketplace-auth-change", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, [location.pathname]);

  const items: SidebarItem[] = [
    {
      label: "Home",
      path: "/marketplace",
      icon: <Home size={18} />,
      requiresProfile: false,
    },
    {
      label: "Dashboard",
      path: "/marketplace/dashboard",
      icon: <LayoutDashboard size={18} />,
      requiresProfile: false,
    },
    {
      label: "Equipment",
      path: "/marketplace/equipment",
      icon: <Video size={18} />,
      requiresProfile: false,
    },
    {
      label: "Talent",
      path: "/marketplace/talent",
      icon: <Users size={18} />,
      requiresProfile: false,
    },
    {
      label: "Team",
      path: "/marketplace/team",
      icon: <UserCheck size={18} />,
      requiresProfile: true,
    },
    {
      label: "Sell / Rent",
      path: "/marketplace/sell",
      icon: <Camera size={18} />,
      requiresProfile: true,
    },
    {
      label: "Post Job",
      path: "/marketplace/post-requirement",
      icon: <PlusCircle size={18} />,
      requiresProfile: true,
    },
    {
      label: "Received Apps",
      path: "/marketplace/received-applications",
      icon: <FileText size={18} />,
      requiresProfile: true,
    },
    {
      label: "Custom Quotes",
      path: "/marketplace/my-orders",
      icon: <Send size={18} />,
      requiresProfile: true,
    },
  ];

  const savedItem: SidebarItem = {
    label: "Saved",
    path: "/marketplace/saved",
    icon: <Bookmark size={18} />,
    requiresProfile: false,
  };

  const handleItemClick = (item: SidebarItem) => {
    if (!loggedIn && item.path !== "/marketplace" && item.path !== "/marketplace/equipment" && item.path !== "/marketplace/talent") {
      showErrorToast("Please login first.");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }

    if (isAccountFrozen() && (item.path === "/marketplace/sell" || item.path === "/marketplace/post-requirement")) {
      showErrorToast("Your marketplace account is frozen or deactivated by an administrator.");
      return;
    }

    if (item.requiresProfile && !hasProfile) {
      showErrorToast("Create profile to access marketplace");
      navigate("/marketplace/profile/create");
      return;
    }

    navigate(item.path);
  };

  return (
    <>
      <Overlay $show={isOpen} onClick={() => setIsOpen(false)} />
      <SidebarWrap $isOpen={isOpen}>
        <LogoArea onClick={() => navigate("/marketplace")}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
            <LogoImg src={theme === "dark" ? kwikMemoriesWhiteLogoIcon : kwikMemoriesLogoIcon} alt="KwikMemories Logo" />
            <LogoText>KwikMemories</LogoText>
            <LogoBadge>Marketplace</LogoBadge>
          </div>
          <CloseDrawerBtn onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} title="Close menu">
            <X size={18} />
          </CloseDrawerBtn>
        </LogoArea>
        <SidebarNav>
          {items.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavItem
                key={item.path}
                $active={isActive}
                onClick={() => handleItemClick(item)}
              >
                <IconWrap $active={isActive}>{item.icon}</IconWrap>
                <LinkLabel $active={isActive}>{item.label}</LinkLabel>
              </NavItem>
            );
          })}

          {/* Saved — only visible when logged in */}
          {loggedIn && (() => {
            const isActive = location.pathname === savedItem.path;
            return (
              <NavItem
                key={savedItem.path}
                $active={isActive}
                onClick={() => navigate(savedItem.path)}
              >
                <IconWrap $active={isActive}>{savedItem.icon}</IconWrap>
                <LinkLabel $active={isActive}>{savedItem.label}</LinkLabel>
              </NavItem>
            );
          })()}
        </SidebarNav>
      </SidebarWrap>
    </>
  );
};

export default MarketplaceSidebar;

// ─── Styled Components ─────────────────────────────────────────────────────────

const SidebarWrap = styled.aside<{ $isOpen: boolean }>`
  display: none;
  background: var(--mp-cardBackground, #ffffff);
  border-right: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  padding: 0;
  flex-direction: column;
  gap: 18px;
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  max-width: 85vw;
  height: 100vh;
  overflow-y: auto;
  z-index: 10002;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: ${p => p.$isOpen ? "0 0 40px rgba(0, 0, 0, 0.3)" : "none"};
  transform: ${p => p.$isOpen ? "translateX(0)" : "translateX(-100%)"};

  @media (max-width: 768px) {
    display: flex;
  }
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 16px;
`;

const NavItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.06)" : "transparent")};
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.06)" : "rgba(255, 255, 255, 0.05)")};
    transform: translateX(2px);
  }
`;

const IconWrap = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$active ? "#c94daa" : "var(--mp-textColor, rgba(23, 23, 23, 0.55))")};
  transition: color 0.2s;
`;

const LinkLabel = styled.span<{ $active: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => (p.$active ? "#c94daa" : "var(--mp-textColor, rgba(23, 23, 23, 0.75))")};
  font-family: Urbanist, sans-serif;
  transition: color 0.2s;
`;

const LogoArea = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
  height: 64px;
  min-height: 64px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  box-sizing: border-box;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const LogoImg = styled.img`
  height: 28px;
  width: 28px;
  object-fit: contain;
  flex-shrink: 0;
`;

const LogoText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13.5px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.02em;
  white-space: nowrap;
`;

const LogoBadge = styled.span`
  font-size: 8.5px;
  font-weight: 700;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.08);
  padding: 2px 5px;
  border-radius: 20px;
  margin-left: 2px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CloseDrawerBtn = styled.button`
  display: none;
  background: none;
  border: none;
  color: rgba(23, 23, 23, 0.55);
  cursor: pointer;
  padding: 4px;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #c94daa;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Overlay = styled.div<{ $show: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${p => p.$show ? "block" : "none"};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(3px);
    z-index: 10001;
  }
`;
